import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IoSend } from 'react-icons/io5';
import { useI18n } from '../contexts/I18n';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getBookingIntent } from '../utils/bookingIntent';
import './AISupport.css';

const WHATSAPP_HELP =
  'https://api.whatsapp.com/send/?phone=+447748628476&text&type=phone_number&app_absent=0';
const FEEDBACK_KEY = 'ehgezly_ai_support_feedback';
const TYPING_MS = 200;
const UNKNOWN_ID = 'unknown';

const QUESTION_KEYWORDS = {
  'how-to-book': ['how to book', 'book appointment', 'book a', 'احجز', 'ازاي احجز', 'حجز موعد', 'عايز احجز'],
  'cancel-appointment': ['cancel', 'cancellation', 'الغي', 'الغى', 'ألغي', 'الغاء', 'إلغاء', 'اكنسل'],
  'booking-pending': ['pending', 'قيد الانتظار', 'still pending', 'waiting for salon', 'لسه pending', 'مستني الرد'],
  'booking-not-available': ['not available', 'unavailable', 'declined', 'غير متاح', 'مش متاح', 'رفض'],
  'active-booking-block': ['already have', 'already booked', "can't book", 'cannot book', 'عندي حجز', 'حجز نشط', 'مش قادر احجز'],
  'leave-review': ['leave a review', 'leave review', 'review', 'rate', 'rating', 'تقييم', 'قيّم', 'قيم', 'ريفيو'],
  'booking-failed': ['booking failed', 'failed', 'فشل الحجز', 'فشل', 'مش راضي يحجز'],
  'no-availability': ['no slots', 'no times', 'no availability', 'no available', 'مفيش مواعيد', 'لا توجد أوقات'],
  'create-account': ['sign up', 'signup', 'register', 'create account', 'إنشاء حساب', 'سجل', 'حساب جديد'],
  'verify-email': ['verify', 'verification', 'email code', 'did not get the code', 'تفعيل', 'كود الإيميل', 'كود الايميل', 'لم يصل', 'مفيش كود'],
  'forgot-password': ['password', 'reset password', 'forgot', 'كلمة السر', 'كلمة المرور', 'نسيت'],
  'update-profile': ['update profile', 'edit profile', 'profile photo', 'الملف الشخصي', 'بروفايل'],
  'delete-account': ['delete account', 'حذف الحساب', 'امسح حسابي'],
  'change-language': ['arabic', 'english', 'language', 'عربي', 'انجليزي', 'الإنجليزية', 'لغة'],
  'find-business': ['find a salon', 'find salon', 'near me', 'explore', 'صالون قريب', 'القي صالون', 'أدور على صالون'],
  'list-business': ['list my salon', 'list salon', 'list business', 'add salon', 'أضف نشاط', 'أضيف صالون', 'ضيف صالوني'],
  'business-hours': ['hours', 'opening hours', 'open today', 'مواعيد العمل', 'ساعات العمل'],
  'service-prices': ['price', 'prices', 'cost', 'سعر', 'اسعار', 'أسعار'],
  'business-reviews': ['read review', 'read reviews', 'reviews', 'تقييمات'],
  'app-not-working': ['not working', 'broken', 'error', 'مش شغال', 'لا يعمل'],
  'notifications-not-working': ['notification', 'notifications', 'اشعار', 'إشعار', 'اشعارات', 'إشعارات'],
  'slow-loading': ['slow', 'loading slowly', 'بطيء', 'بطئ', 'تحميل'],
  'data-privacy': ['privacy', 'data protected', 'خصوصية', 'بياناتي'],
  'contact-human': ['human', 'whatsapp', 'contact', 'واتساب', 'شخص', 'دعم بشري'],
};

const dashPath = (isAuthenticated) =>
  isAuthenticated ? '/dashboard' : '/login?redirect=/dashboard';

const getBookPath = (isAuthenticated, pathname, shopNameParam) => {
  if (shopNameParam) return `/booking/${encodeURIComponent(shopNameParam)}`;
  const bookingMatch = String(pathname || '').match(/^\/booking\/([^/?#]+)/);
  if (bookingMatch) return `/booking/${bookingMatch[1]}`;
  const intent = getBookingIntent();
  if (intent?.shopName) return `/booking/${encodeURIComponent(intent.shopName)}`;
  return isAuthenticated ? '/explore' : '/';
};

const getQuestionCtas = (questionId, isAuthenticated, bookPath) => {
  const dash = dashPath(isAuthenticated);
  switch (questionId) {
    case 'how-to-book':
      return [{ to: bookPath, labelKey: 'aiBookAppointment' }];
    case 'cancel-appointment':
    case 'booking-failed':
    case 'booking-pending':
    case 'active-booking-block':
      return [{ to: dash, labelKey: 'aiViewBookings' }];
    case 'no-availability':
    case 'booking-not-available':
    case 'business-hours':
    case 'service-prices':
      return [{ to: isAuthenticated ? '/explore' : '/', labelKey: 'aiCtaBrowseSalons' }];
    case 'create-account':
      return [{ to: '/register', labelKey: 'aiCtaSignUp' }];
    case 'verify-email':
      return [{ to: isAuthenticated ? '/dashboard' : '/login', labelKey: 'aiCtaVerifyEmail' }];
    case 'forgot-password':
      return [{ to: '/login', labelKey: 'aiCtaResetPassword' }];
    case 'update-profile':
      return [{ to: dash, labelKey: 'aiCtaEditProfile' }];
    case 'delete-account':
      return [{ to: '/delete-account', labelKey: 'aiCtaDeleteAccount' }];
    case 'change-language':
      return [{ action: 'toggleLang', labelKey: 'aiCtaToggleLang' }];
    case 'find-business':
      return [
        isAuthenticated
          ? { to: '/explore', labelKey: 'aiCtaExplore' }
          : { to: '/', labelKey: 'aiCtaBrowseSalons' },
      ];
    case 'list-business':
      return [{ to: '/ListUrBussiness', labelKey: 'aiCtaListBusiness' }];
    case 'business-reviews':
    case 'leave-review':
      return [{ to: dash, labelKey: 'aiCtaLeaveReview' }];
    case 'app-not-working':
    case 'slow-loading':
      return [{ to: '/contact', labelKey: 'aiCtaContact' }];
    case 'notifications-not-working':
      return [{ to: dash, labelKey: 'aiViewBookings' }];
    case 'data-privacy':
      return [{ to: '/privacy', labelKey: 'aiCtaPrivacy' }];
    case 'contact-human':
    case UNKNOWN_ID:
      return [{ href: WHATSAPP_HELP, labelKey: 'aiCtaWhatsApp' }];
    default:
      return [];
  }
};

const normalizeHelpText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[؟?!.،,._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findAnswerForInput = (raw, categories) => {
  const q = normalizeHelpText(raw);
  if (q.length < 2) return null;

  let best = null;
  let bestScore = 0;
  for (const category of categories) {
    for (const item of category.questions) {
      let score = 0;
      const nQuestion = normalizeHelpText(item.question);
      if (nQuestion && (q.includes(nQuestion) || nQuestion.includes(q))) score += 14;
      for (const kw of QUESTION_KEYWORDS[item.id] || []) {
        const k = normalizeHelpText(kw);
        if (!k || k.length < 2) continue;
        if (q.includes(k)) score += Math.min(k.length, 16);
      }
      if (score > bestScore) {
        bestScore = score;
        best = { ...item, category };
      }
    }
  }
  return bestScore >= 4 ? best : null;
};

const saveHelpFeedback = ({ questionId, question, helpful }) => {
  try {
    const prev = JSON.parse(localStorage.getItem(FEEDBACK_KEY) || '[]');
    const next = Array.isArray(prev) ? prev : [];
    next.push({
      questionId: questionId || UNKNOWN_ID,
      question: question || '',
      helpful: !!helpful,
      at: new Date().toISOString(),
    });
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(next.slice(-200)));
  } catch {
    /* ignore quota / private mode */
  }
};

const AISupport = ({ onOpenChange, embedded = false }) => {
  const { t, lang } = useI18n();
  const { toggleLang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { shopName: shopNameParam } = useParams();
  const [isOpen, setIsOpen] = useState(embedded);
  useEffect(() => {
    if (typeof onOpenChange === 'function') onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const chatMessagesRef = useRef(null);
  const typingTimerRef = useRef(null);

  const bookPath = getBookPath(isAuthenticated, location.pathname, shopNameParam);

  const supportCategories = useMemo(() => [
    {
      id: 'booking',
      title: `📅 ${t('aiCategoryBooking')}`,
      icon: '📅',
      questions: [
        { id: 'how-to-book', question: t('aiQHowToBook'), answer: t('aiAHowToBook') },
        { id: 'booking-pending', question: t('aiQBookingPending'), answer: t('aiABookingPending') },
        { id: 'booking-not-available', question: t('aiQBookingNotAvailable'), answer: t('aiABookingNotAvailable') },
        { id: 'active-booking-block', question: t('aiQActiveBookingBlock'), answer: t('aiAActiveBookingBlock') },
        { id: 'cancel-appointment', question: t('aiQCancelAppointment'), answer: t('aiACancelAppointment') },
        { id: 'leave-review', question: t('aiQLeaveReview'), answer: t('aiALeaveReview') },
        { id: 'booking-failed', question: t('aiQBookingFailed'), answer: t('aiABookingFailed') },
        { id: 'no-availability', question: t('aiQNoAvailability'), answer: t('aiANoAvailability') },
      ]
    },
    {
      id: 'account',
      title: `👤 ${t('aiCategoryAccount')}`,
      icon: '👤',
      questions: [
        { id: 'create-account', question: t('aiQCreateAccount'), answer: t('aiACreateAccount') },
        { id: 'verify-email', question: t('aiQVerifyEmail'), answer: t('aiAVerifyEmail') },
        { id: 'forgot-password', question: t('aiQForgotPassword'), answer: t('aiAForgotPassword') },
        { id: 'update-profile', question: t('aiQUpdateProfile'), answer: t('aiAUpdateProfile') },
        { id: 'change-language', question: t('aiQChangeLanguage'), answer: t('aiAChangeLanguage') },
        { id: 'delete-account', question: t('aiQDeleteAccount'), answer: t('aiADeleteAccount') },
      ]
    },
    {
      id: 'business',
      title: `🏪 ${t('aiCategoryBusiness')}`,
      icon: '🏪',
      questions: [
        { id: 'find-business', question: t('aiQFindBusiness'), answer: t('aiAFindBusiness') },
        { id: 'list-business', question: t('aiQListBusiness'), answer: t('aiAListBusiness') },
        { id: 'business-hours', question: t('aiQBusinessHours'), answer: t('aiABusinessHours') },
        { id: 'service-prices', question: t('aiQServicePrices'), answer: t('aiAServicePrices') },
        { id: 'business-reviews', question: t('aiQBusinessReviews'), answer: t('aiABusinessReviews') },
      ]
    },
    {
      id: 'technical',
      title: `🔧 ${t('aiCategoryTechnical')}`,
      icon: '🔧',
      questions: [
        { id: 'app-not-working', question: t('aiQAppNotWorking'), answer: t('aiAAppNotWorking') },
        { id: 'notifications-not-working', question: t('aiQNotificationsNotWorking'), answer: t('aiANotificationsNotWorking') },
        { id: 'slow-loading', question: t('aiQSlowLoading'), answer: t('aiASlowLoading') },
        { id: 'data-privacy', question: t('aiQDataPrivacy'), answer: t('aiADataPrivacy') },
        { id: 'contact-human', question: t('aiQContactHuman'), answer: t('aiAContactHuman') },
      ]
    }
  ], [t]);

  const addToChat = (message, sender) => {
    const newMessage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: message,
      sender: sender,
      timestamp: new Date().toLocaleTimeString()
    };
    setChatHistory(prev => [...prev, newMessage]);
  };

  const replyWith = (question) => {
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    setIsTyping(true);
    setShowFeedback(false);
    setFeedbackGiven(false);
    typingTimerRef.current = window.setTimeout(() => {
      addToChat(question.answer, 'ai');
      setIsTyping(false);
      setShowFeedback(true);
      setFeedbackGiven(false);
      typingTimerRef.current = null;
    }, TYPING_MS);
  };

  useEffect(() => () => {
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
  }, []);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCurrentStep(1);
  };

  const handleQuestionSelect = (question, category = selectedCategory) => {
    setSelectedCategory(category || selectedCategory);
    setSelectedQuestion(question);
    setCurrentStep(2);
    addToChat(question.question, 'user');
    replyWith(question);
  };

  const getRelatedQuestions = (currentQuestionId) => {
    if (!selectedCategory) return [];
    return selectedCategory.questions
      .filter((q) => q.id !== currentQuestionId)
      .slice(0, 3);
  };

  const handleFeedback = (helpful) => {
    saveHelpFeedback({
      questionId: selectedQuestion?.id || UNKNOWN_ID,
      question: selectedQuestion?.question || chatHistory.find((m) => m.sender === 'user')?.text || '',
      helpful,
    });
    setFeedbackGiven(true);
    setShowFeedback(false);
    addToChat(helpful ? t('aiFeedbackYes') : t('aiFeedbackNo'), 'ai');
  };

  const handleUserInput = (e) => {
    e?.preventDefault?.();
    const question = userInput.trim();
    if (!question) return;

    setUserInput('');
    addToChat(question, 'user');
    setCurrentStep(2);

    const match = findAnswerForInput(question, supportCategories);
    if (match) {
      setSelectedQuestion(match);
      setSelectedCategory(match.category);
      replyWith(match);
      return;
    }

    setSelectedQuestion({ id: UNKNOWN_ID, question, answer: t('aiNoSpecificAnswer') });
    setSelectedCategory(null);
    replyWith({ answer: t('aiNoSpecificAnswer') });
  };

  const scrollChatToBottom = () => {
    const inner = chatMessagesRef.current;
    if (!inner) return;
    inner.scrollTop = inner.scrollHeight;
    const outer = inner.closest('.ai-support-content');
    if (outer) outer.scrollTop = outer.scrollHeight;
  };

  useLayoutEffect(() => {
    if (currentStep !== 2) return undefined;
    scrollChatToBottom();
    const raf = requestAnimationFrame(() => {
      scrollChatToBottom();
      requestAnimationFrame(scrollChatToBottom);
    });
    const timer = window.setTimeout(scrollChatToBottom, 80);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [chatHistory, isTyping, showFeedback, feedbackGiven, currentStep]);

  const resetChat = () => {
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    setCurrentStep(0);
    setSelectedCategory(null);
    setSelectedQuestion(null);
    setChatHistory([]);
    setIsTyping(false);
    setUserInput('');
    setShowFeedback(false);
    setFeedbackGiven(false);
  };

  const goToCta = (to) => {
    resetChat();
    if (!embedded) setIsOpen(false);
    navigate(to);
  };

  const handleCta = (cta) => {
    if (cta.action === 'toggleLang') {
      toggleLang();
      return;
    }
    if (cta.href) {
      window.open(cta.href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (cta.to) goToCta(cta.to);
  };

  const getQuickActions = () => {
    const quickActions = [
      { text: t('aiBookAppointment'), action: () => goToCta(bookPath) },
      { text: t('aiViewBookings'), action: () => goToCta(dashPath(isAuthenticated)) },
    ];

    return quickActions.map((action, index) => (
      <button
        key={index}
        className="quick-action-btn"
        onClick={action.action}
      >
        {action.text}
      </button>
    ));
  };

  const answerCtas =
    !isTyping && selectedQuestion
      ? getQuestionCtas(selectedQuestion.id, isAuthenticated, bookPath).slice(0, 1)
      : [];

  const questionInput = (
    <form className={`ai-chat-input${currentStep === 2 ? '' : ' ai-chat-input--home'}`} onSubmit={handleUserInput}>
      <input
        type="text"
        className="ai-chat-input__field"
        placeholder={t('aiTypeYourQuestion')}
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        aria-label={t('aiTypeYourQuestion')}
      />
      <button
        type="submit"
        className="ai-chat-input__send"
        disabled={!userInput.trim()}
        aria-label={t('aiTypeYourQuestion')}
      >
        <IoSend />
      </button>
    </form>
  );

  const supportBody = (
    <>
      {currentStep === 0 && (
        <div className="categories-section">
          <h4>{t('aiWhatDoYouNeed')}</h4>
          <div className="categories-grid">
            {supportCategories
              .filter(category => category.id !== 'payments')
              .map(category => (
              <button
                key={category.id}
                className="category-card"
                onClick={() => handleCategorySelect(category)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-title">{category.title.replace(`${category.icon} `, '')}</span>
              </button>
            ))}
          </div>
          
          <div className="quick-actions">
            <h4>{t('aiQuickActions')}</h4>
            <div className="quick-actions-grid">
              {getQuickActions()}
            </div>
          </div>
          {questionInput}
        </div>
      )}

      {currentStep === 1 && selectedCategory && (
        <div className="questions-section">
          <div className="breadcrumb">
            <button
              onClick={() => {
                setCurrentStep(0);
                setSelectedCategory(null);
              }}
            >
              {t('aiBackToCategories')}
            </button>
          </div>
          <h4 className="category-title-heading">{selectedCategory.title}</h4>
          
          <div className="questions-list">
            {selectedCategory.questions.map(question => (
              <button
                key={question.id}
                className="question-item"
                onClick={() => handleQuestionSelect(question)}
              >
                <span className="question-text">{question.question}</span>
                <span className="question-arrow">{lang === 'ar' ? '←' : '→'}</span>
              </button>
            ))}
          </div>
          {questionInput}
        </div>
      )}

      {currentStep === 2 && (
        <div className="chat-section">
          <div className="breadcrumb">
            <button
              onClick={() => {
                if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
                if (selectedCategory) {
                  setCurrentStep(1);
                } else {
                  setCurrentStep(0);
                }
                setSelectedQuestion(null);
                setChatHistory([]);
                setIsTyping(false);
                setUserInput('');
                setShowFeedback(false);
                setFeedbackGiven(false);
              }}
            >
              {selectedCategory ? t('aiBackToQuestions') : t('aiBackToCategories')}
            </button>
          </div>

          <div className="chat-messages" ref={chatMessagesRef}>
            {chatHistory.map(message => (
              <div
                key={message.id}
                className={`message ${message.sender}`}
              >
                <div className="message-content">
                  <p>{message.text}</p>
                  <span className="message-time">{message.timestamp}</span>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="message ai typing">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            {showFeedback && !feedbackGiven && !isTyping && (
              <div className="ai-feedback-section">
                <p className="ai-feedback-text">{t('aiWasThisHelpful')}</p>
                <div className="ai-feedback-buttons">
                  <button
                    type="button"
                    className="ai-feedback-btn ai-feedback-btn--yes"
                    onClick={() => handleFeedback(true)}
                  >
                    {t('aiYes')}
                  </button>
                  <button
                    type="button"
                    className="ai-feedback-btn ai-feedback-btn--no"
                    onClick={() => handleFeedback(false)}
                  >
                    {t('aiNo')}
                  </button>
                </div>
              </div>
            )}

            {selectedQuestion?.id && selectedQuestion.id !== UNKNOWN_ID && !isTyping && getRelatedQuestions(selectedQuestion.id).length > 0 && (
              <div className="ai-related-section">
                <p className="ai-related-title">{t('aiRelatedQuestions')}</p>
                {getRelatedQuestions(selectedQuestion.id).map((question) => (
                  <button
                    key={question.id}
                    type="button"
                    className="ai-related-item"
                    onClick={() => handleQuestionSelect(question)}
                  >
                    {question.question}
                  </button>
                ))}
              </div>
            )}

            {answerCtas.map((cta) => (
              <button
                key={cta.labelKey}
                type="button"
                className="action-btn primary ai-book-cta"
                onClick={() => handleCta(cta)}
              >
                {t(cta.labelKey)}
              </button>
            ))}
          </div>

          {questionInput}
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div
        className="ai-support-modal ai-support-modal--embedded"
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
        role="region"
        aria-label={t('aiAssistantTitle')}
      >
        <div className="ai-support-header">
          <div className="ai-avatar">
            <span>🤖</span>
          </div>
          <div className="ai-info">
            <h3>{t('aiAssistantTitle')}</h3>
            <p>{t('aiAssistantSubtitle')}</p>
          </div>
        </div>
        <div className="ai-support-content">
          {supportBody}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        className={`ai-support-btn ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('aiSupportOpen')}
      >
        <span className="ai-icon">🤖</span>
      </button>

      {isOpen && (
        <div className="ai-support-modal" dir={lang === 'ar' ? 'rtl' : 'ltr'} role="dialog" aria-label={t('aiAssistantTitle')}>
          <div className="ai-support-header">
            <div className="ai-avatar">
              <span>🤖</span>
            </div>
            <div className="ai-info">
              <h3>{t('aiAssistantTitle')}</h3>
              <p>{t('aiAssistantSubtitle')}</p>
            </div>
            <button
              className="close-btn"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          <div className="ai-support-content">
            {supportBody}
          </div>
        </div>
      )}
    </>
  );
};

export default AISupport;
